import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { format, isValid, parse } from 'date-fns';
import { cloneDeepWith } from 'lodash';
import cloneDeep from 'lodash/cloneDeep';
import {
  AddressDto,
  BranchOfficeDto,
  CardApplicationDto,
  CardProfileDto,
  CardProfileHeaderDto,
  CardTypesEnum,
  CreateCardApplicationWithAccountCommand,
  CustomerCardInfoVm,
  CustomerDetailVm,
  CustomerDto,
  CustomerProfileDto,
  PersonDto,
} from '@/api';
import { InternalCardApplicationDto, InternalCardProfileDto } from '@ui-kit';
import { _branchOffice } from '@/constants/BranchOffice';
import { _address } from '@/constants/Address';
import { DATE_FORMAT, DATE_PICKER_FORMAT, isDateOneAfterDateTwo, isDateOneSameOrBeforeDateTwo } from '@/utils/dateTime';
import isDefined, { isArrayEmpty } from '@common/utils/isDefined';
import { shouldSetValidTo } from '@/utils/customerProfileValidToDefault';
import type { CardApplicationDialogTypeEnum } from '@/components/cardApplication/cardApplicationDialogType';
import {
  getCardProfilesWithInternalKey,
  getCardProfileWithInternalKey,
  getNewCardProfile,
  updateProfilePositions,
} from '@common/utils/cardProfile';
import { emptyCustodianData } from './useCustomerStore';
import { useSystemConfigurationStore } from './useSystemConfigurationStore';

export const REGISTER_NEW_BANK_CARD = 'REGISTER_NEW_BANK_CARD';

const emptyCardData = {
  serialNumber: 0,
  applicationNumber: '',
  id: '',
  note: '',
  created: '',
  keepPhoto: false,
  state: {
    id: '',
    name: '',
    displayName: '',
  },
  customerChanges: [],
  customer: {
    id: '',
    firstName: '',
    lastName: '',
    fullName: '',
    birthDay: '',
    created: '',
    draft: false,
    originId: '',
  },
  externalCardCardNumber: '',
  externalCardValidTo: '',
  cardProfiles: [
    {
      position: 1,
      profile: {
        id: '',
        name: '',
      },
      validFrom: '',
      validTo: '',
      confirmationChecked: '',
    },
  ],
  contactMeIfCardFound: false,
  recipientAddress: {
    street: '',
    streetNumber: '',
    city: '',
    zip: '',
  },
  deliveryBranchOffice: {
    id: '',
    name: '',
    branchNumber: 0,
    address: {
      street: '',
      streetNumber: '',
      city: '',
      zip: '',
    },
  },
  eshop: true,
  paid: false,
  checkedBy: {} as PersonDto,
  checkedDate: '',
  cardForFree: false,
} as unknown as CardApplicationDto;

const emptyCustomerData = {
  id: '',
  firstName: '',
  lastName: '',
  fullName: '',
  birthDay: '',
  photo: '',
  email: '',
  phoneNumber: '',
  originId: '',
  address: {
    street: '',
    streetNumber: '',
    city: '',
    zip: '',
  },
  parent: { address: {} } as CustomerDto,
  accountEmail: '',
  virtualAccount: '',
  draft: false,
  changes: [],
  lastCard: undefined,
  pin: '',
  external: false,
  isFake: false,
} as CustomerDetailVm;

export type NewCustomerAccountData = Pick<
  CreateCardApplicationWithAccountCommand,
  'accountUsername' | 'accountPassword' | 'accountMobileExclusive'
>;
const emptyNewCustomerAccountData: NewCustomerAccountData = {
  accountUsername: '',
  accountPassword: '',
  accountMobileExclusive: true,
};

const emptyProfilesAttachment: ProfilesAttachment = {
  profileAttachments: {},
};

export interface Attachment {
  id: string;
  file: File;
}

interface ProfilesAttachment {
  profileAttachments: Record<number, Attachment[]>;
}

export interface AttachmentPayload {
  attachmentTypeId: string;
  attachmentFile: File;
  profileInternalKey: number;
}

export interface SetVerifiedProfilePayload {
  customerDetail: CustomerDetailVm;
  profiles?: CardProfileDto[];
}

export const useCardApplicationStore = defineStore('cardApplication', () => {
  const cardData = ref<InternalCardApplicationDto>(cloneDeepWith(emptyCardData, getCardProfilesWithInternalKey));
  const originalCardProfilesData = ref<CardProfileDto[] | null>(null);
  const customerData = ref<CustomerDetailVm>(cloneDeep(emptyCustomerData));
  const custodianData = ref<CustomerDto>(cloneDeep(emptyCustodianData));
  const custodianAdded = ref(false);
  const deliveryBranchOfficeData = ref<BranchOfficeDto>(cloneDeep(_branchOffice));
  const recipientAddressData = ref<AddressDto>(cloneDeep(_address));
  const termsAndConditionsApproval = ref(false);
  const gdprApproval = ref(false);
  const marketingConsent = ref(true);
  const customerProfileAutoUpdated = ref(false);
  // internalKey -> true for every cardProfile slot that was hydrated from a customer's
  // already-approved card (set by tryAutoUpdateCustomerProfile). Lets consumers tell
  // "existing approved slot" from "freshly-added slot" without piggybacking on the UI
  // `disabled` flag (which means "input is read-only" — a different concern).
  const slotsLoadedFromApprovedCard = ref<Record<number, true>>({});
  const profilesAreInCollision = ref(false);
  const attachments = ref<ProfilesAttachment>(cloneDeep(emptyProfilesAttachment));
  const bankCardId = ref('');
  const portableBankCardData = ref<CustomerCardInfoVm | undefined>(undefined);
  const useExistingCustomerForBankCardPersonalization = ref(false);
  const cardType = ref<CardTypesEnum>();
  const dialogType = ref<CardApplicationDialogTypeEnum>();
  const newCustomerAccountData = ref({ ...emptyNewCustomerAccountData });
  const systemConfigurationStore = useSystemConfigurationStore();

  const isBankCardType = computed(() => {
    return cardType.value === CardTypesEnum.BANK_CARD;
  });

  const isVirtualCardType = computed(() => {
    return cardType.value === CardTypesEnum.MOBILE_PHONE;
  });

  const isCustodianAdded = computed(() => {
    return custodianAdded.value || !!custodianData.value.id;
  });

  const isCustomerFilled = computed(() => {
    return !!customerData.value.id;
  });

  const isCustomerVerified = computed(() => {
    return isDefined(customerData.value.personalDataVerifiedDate);
  });

  const emptyAttachments = computed(() => {
    return isArrayEmpty(Object.keys(attachments.value.profileAttachments));
  });

  const customerDateBirthday = computed(() => {
    return customerData.value.birthDay && isValid(parse(customerData.value.birthDay, DATE_PICKER_FORMAT, new Date()))
      ? format(parse(customerData.value.birthDay, DATE_PICKER_FORMAT, new Date()), DATE_FORMAT)
      : '';
  });

  const cardProfiles = computed(() => {
    return cardData.value.cardProfiles;
  });

  const setCustomerData = (payload: CustomerDetailVm) => {
    payload.birthDay = formatBirthday(payload.birthDay);
    customerData.value = payload;
  };

  const setCardData = (payload: InternalCardApplicationDto | CardApplicationDto) => {
    cardData.value = cloneDeepWith(payload, getCardProfilesWithInternalKey);
  };

  const setCustomerProfilesData = (cardProfiles: InternalCardProfileDto[]) => {
    cardData.value.cardProfiles = cardProfiles;
  };

  const setOriginalCardProfilesData = (cardProfiles: CardProfileDto[]) => {
    originalCardProfilesData.value = cloneDeep(cardProfiles);
  };

  const initCardData = () => {
    cardData.value = cloneDeepWith(emptyCardData, getCardProfilesWithInternalKey);
    slotsLoadedFromApprovedCard.value = {};
  };

  const isSlotLoadedFromApprovedCard = (internalKey: number): boolean =>
    !!slotsLoadedFromApprovedCard.value[internalKey];

  const setBranchOfficeData = (payload: BranchOfficeDto) => {
    deliveryBranchOfficeData.value = payload;
  };

  const setBankCardId = (id: string) => {
    bankCardId.value = id;
  };

  const setPortableBankCardData = (data: CustomerCardInfoVm) => {
    portableBankCardData.value = data;
  };

  const setUseExistingCustomerForBankCardPersonalization = (value: boolean) => {
    useExistingCustomerForBankCardPersonalization.value = value;
  };

  const setNewExternalCard = ({ id, readonly }: { id: string; readonly?: boolean }) => {
    cardData.value.externalCardId = id;
    if (!readonly) {
      cardData.value.externalCardCardNumber = '';
      cardData.value.externalCardValidTo = '';
    }
  };

  const setExistingExternalCard = (card: CustomerCardInfoVm) => {
    cardData.value.externalCardId = card.id;
    cardData.value.externalCardCardNumber = card.cardNumber;
    cardData.value.externalCardValidTo = card.validTo;
  };

  const setRecipientAddressData = (payload: AddressDto) => {
    recipientAddressData.value = payload;
  };

  const setCustomerPhoto = (payload: string) => {
    customerData.value.photo = payload;
  };

  const setCustodianData = (payload: CustomerDto) => {
    payload.birthDay = formatBirthday(payload.birthDay);
    custodianData.value = payload;
  };

  const resetCustodian = () => {
    custodianData.value = cloneDeep(emptyCustodianData);
    custodianAdded.value = false;
  };

  const clearCustodian = () => {
    custodianData.value = cloneDeep(emptyCustodianData);
  };

  const setCardType = (value: CardTypesEnum) => {
    cardType.value = value;
  };

  const setDialogType = (value: CardApplicationDialogTypeEnum) => {
    dialogType.value = value;
  };

  const setNewCustomerAccountData = (payload: NewCustomerAccountData) => {
    newCustomerAccountData.value = payload;
  };

  const setCustodianAdded = (isCustodianAdded: boolean) => {
    custodianAdded.value = isCustodianAdded;
  };

  const resetBranchOfficeData = () => {
    deliveryBranchOfficeData.value = cloneDeep(_branchOffice);
  };

  /**
   * Reset concrete bank card id. New bank card id should be preserved to properly init tokenization.
   */
  const resetConcreteBankCardId = () => {
    if (bankCardId.value !== REGISTER_NEW_BANK_CARD) {
      bankCardId.value = '';
    }
  };

  const resetBankCardId = () => {
    bankCardId.value = '';
  };

  const resetRecipientAddressData = () => {
    recipientAddressData.value = cloneDeep(_address);
  };

  const setProfilesInCollision = (collision: boolean) => {
    profilesAreInCollision.value = collision;
  };

  const initCardApplication = () => {
    cardData.value = cloneDeepWith(emptyCardData, getCardProfilesWithInternalKey);
    customerData.value = cloneDeep(emptyCustomerData);
    custodianData.value = cloneDeep(emptyCustodianData);
    deliveryBranchOfficeData.value = cloneDeep(_branchOffice);
    recipientAddressData.value = cloneDeep(_address);
    newCustomerAccountData.value = { ...emptyNewCustomerAccountData };
    termsAndConditionsApproval.value = false;
    gdprApproval.value = false;
    marketingConsent.value = true;
    attachments.value = cloneDeep(emptyProfilesAttachment);
    customerProfileAutoUpdated.value = false;
    slotsLoadedFromApprovedCard.value = {};
    portableBankCardData.value = undefined;
    useExistingCustomerForBankCardPersonalization.value = false;
  };

  const setTermsAndConditionsApproval = (value: boolean) => {
    termsAndConditionsApproval.value = value;
  };

  const setGdprApproval = (value: boolean) => {
    gdprApproval.value = value;
  };

  const setMarketingConsent = (value: boolean) => {
    marketingConsent.value = value;
  };

  const setVerifiedProfile = (payload: SetVerifiedProfilePayload) => {
    customerProfileAutoUpdated.value = false;
    slotsLoadedFromApprovedCard.value = {};
    const { customerDetail, profiles } = payload;
    const oldProfiles = profiles ?? customerDetail.lastCard?.cardProfiles ?? [];

    const profileIsValid = (profile: CardProfileHeaderDto | CardProfileDto) => {
      const { validFrom, validTo } = profile;
      return !validFrom || !validTo || isDateOneSameOrBeforeDateTwo(new Date(), validTo);
    };

    const copyProfileToDto = (
      target: InternalCardProfileDto,
      source: InternalCardProfileDto,
      secondSource: InternalCardProfileDto | undefined,
    ): void => {
      const currentDate = format(new Date(), DATE_FORMAT);
      target.validFrom = isDateOneAfterDateTwo(source.validFrom, currentDate) ? source.validFrom || '' : currentDate;
      if (secondSource) {
        target.validTo = profileIsValid(secondSource) ? source.validTo : '';
      } else {
        target.validTo = (shouldSetValidTo(source.profile as CustomerProfileDto) && source.validTo) || '';
      }

      target.profile = source.profile;
      target.confirmationChecked = source.confirmationChecked;
      target.position = source.position;
    };

    const tryAutoUpdateCustomerProfile = (profiles: InternalCardProfileDto[]): void => {
      for (let i = 0; i < profiles.length; i++) {
        const source = profiles[i];
        const secondSource = profiles[i + 1] ?? undefined;
        if (source) {
          const target = cardData.value.cardProfiles?.[i] ?? getNewCardProfile(source.position);
          target.disabled = true;
          copyProfileToDto(target, source, secondSource);
          slotsLoadedFromApprovedCard.value[target.internalKey] = true;
          cardData.value.cardProfiles[i] = target;
          customerProfileAutoUpdated.value = true;
        }
      }
    };

    if (!systemConfigurationStore.regularProfileAlwaysDefaultInCardApplicationOnEshop) {
      const reusableProfiles: InternalCardProfileDto[] = [];
      for (const profile of oldProfiles) {
        if (profile && profileIsValid(profile)) {
          const internalProfile: InternalCardProfileDto = cloneDeepWith(profile, getCardProfileWithInternalKey);
          reusableProfiles.push(internalProfile);
        }
      }

      tryAutoUpdateCustomerProfile(reusableProfiles);
    }
    // save the initial card profiles data so it can be restored if the user changes validation dates
    // using a profile that doesn't allow collisions, then switches to one that does
    setOriginalCardProfilesData(oldProfiles);
    updateProfilePositions(cardData.value.cardProfiles);
  };

  const setAttachment = (payload: AttachmentPayload) => {
    const attachment: Attachment = {
      id: payload.attachmentTypeId,
      file: payload.attachmentFile,
    };

    if (!(payload.profileInternalKey in attachments.value.profileAttachments)) {
      attachments.value.profileAttachments[payload.profileInternalKey] = [];
    }

    handleAttachments(attachments.value.profileAttachments[payload.profileInternalKey], attachment);
  };

  const clearAttachments = (internalKey?: number) => {
    if (internalKey) {
      attachments.value.profileAttachments[internalKey] = [];
    } else {
      attachments.value = cloneDeep(emptyProfilesAttachment);
    }
  };

  const formatBirthday = (date: string | undefined) => {
    // check date has already proper format
    if (date && format(parse(date, DATE_FORMAT, new Date()), DATE_PICKER_FORMAT) === date) {
      return date;
    }
    return date ? format(date, DATE_PICKER_FORMAT) : '';
  };

  const handleAttachments = (attachments: Attachment[], newAttachment: Attachment) => {
    const index = attachments.findIndex(attachment => attachment.id === newAttachment.id);
    if (index === -1 && newAttachment.file) {
      attachments.push(newAttachment);
    } else if (newAttachment.file) {
      attachments[index] = newAttachment;
    } else {
      attachments.splice(index, 1);
    }
  };

  return {
    cardData,
    originalCardProfilesData,
    customerData,
    custodianData,
    bankCardId,
    portableBankCardData,
    useExistingCustomerForBankCardPersonalization,
    isBankCardType,
    isVirtualCardType,
    isCustodianAdded,
    isCustomerFilled,
    isCustomerVerified,
    emptyAttachments,
    dialogType,
    customerDateBirthday,
    cardProfiles,
    deliveryBranchOfficeData,
    recipientAddressData,
    marketingConsent,
    gdprApproval,
    termsAndConditionsApproval,
    custodianAdded,
    attachments,
    profilesAreInCollision,
    customerProfileAutoUpdated,
    isSlotLoadedFromApprovedCard,
    newCustomerAccountData,
    cardType,

    setCustomerData,
    setOriginalCardProfilesData,
    setCardData,
    setCustomerProfilesData,
    initCardData,
    setBranchOfficeData,
    setBankCardId,
    setPortableBankCardData,
    setUseExistingCustomerForBankCardPersonalization,
    setNewExternalCard,
    setExistingExternalCard,
    setRecipientAddressData,
    setCustomerPhoto,
    setCustodianData,
    resetCustodian,
    clearCustodian,
    setCardType,
    setDialogType,
    setNewCustomerAccountData,
    setCustodianAdded,
    resetBranchOfficeData,
    resetConcreteBankCardId,
    resetBankCardId,
    resetRecipientAddressData,
    setProfilesInCollision,
    initCardApplication,
    setTermsAndConditionsApproval,
    setGdprApproval,
    setMarketingConsent,
    setVerifiedProfile,
    setAttachment,
    clearAttachments,
  };
});
